import {SignUpDTO} from '@/domain/dtos/SignUpDTO';

import {USER_DEVICE} from '../consts';
import {getDefaultAuthQueryParams} from '../lib/getDefaultAuthQueryParams';
import {getMetadata} from '../lib/getMetadata';
import {getRequiredAuthHeaders} from '../lib/getRequiredAuthHeaders';
import {httpGet, httpPost} from '../serve';
import {AuthResponse, SignUpUser} from './types';

export type {SuccessAuthResponse} from './types';

export async function signUpViaGoogle(id_token: string, user: SignUpUser = {}) {
  const data = {
    flow: 'up',
    id_token,
    user_device: USER_DEVICE,
    ...getMetadata(),
    ...user,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(
    `/sign/in/google?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data.result;
}

export async function singUpViaEmail(user: SignUpUser) {
  const formData = new FormData();

  formData.append(
    'content',
    JSON.stringify({
      flow: 'up',
      user_device: USER_DEVICE,
      lang: 'en',
      ...getMetadata(),
      ...user,
    })
  );

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(`/sign/up?${params}`, formData, {
    headers: getRequiredAuthHeaders(),
    withCredentials: true,
  });

  return response.data.result;
}

export async function signUpViaApple(code: string, user: SignUpUser = {}) {
  const data = {
    flow: 'up',
    code_v2: code,
    user_device: USER_DEVICE,
    ...getMetadata(),
    ...user,
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(
    `/sign/in/apple?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data.result;
}

export async function signInViaGoogle(id_token: string) {
  const data = {
    flow: 'in',
    id_token,
    user_device: USER_DEVICE,
    ...getMetadata(),
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(
    `/sign/in/google?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function signInViaApple(code: string) {
  const data = {
    flow: 'in',
    code_v2: code,
    user_device: USER_DEVICE,
    ...getMetadata(),
  };

  const formData = new FormData();
  formData.append('content', JSON.stringify(data));

  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(
    `/sign/in/apple?${params}`,
    formData,
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data.result;
}

export async function reachSignUpLimit() {
  const response = await httpGet<{last: boolean}>('/sign/rate_limit/check');
  return response.data.result;
}

export async function singUpViaPhone(userData: Record<string, unknown> = {}) {
  const params = await getDefaultAuthQueryParams();
  const response = await httpPost<SignUpDTO>(
    `/sign/in/phone?${params}`,
    {
      flow: 'up',
      user_device: USER_DEVICE,
      lang: 'en',
      dob: `${userData.day}.${userData.month}.${userData.year}`,
      ...userData,
      ...getMetadata(),
    },
    {
      headers: getRequiredAuthHeaders(),
      withCredentials: true,
    }
  );

  return response.data;
}

export async function canSignInViaPhone(phone: string) {
  const params = await getDefaultAuthQueryParams();
  params.append('lite', '1');

  const response = await httpPost<AuthResponse>(
    `/sign/in/phone?${params}`,
    {
      phone,
    },
    {
      headers: getRequiredAuthHeaders(),
    }
  );
  return response.data;
}

export async function requestPhoneConfirmation() {
  const response = await httpPost('/phone_confirmation', undefined, {
    headers: getRequiredAuthHeaders(),
    withCredentials: true,
  });

  return response.data;
}

export async function confirmPhoneCode({
  phone,
  code,
}: {
  phone: string;
  code: string;
}) {
  let normalizedPhone = phone || '';
  if (!normalizedPhone.startsWith('+')) {
    normalizedPhone = `+${normalizedPhone}`;
  }

  const {data} = await httpPost(
    '/phone_confirmation',
    {
      phone: normalizedPhone,
      code,
    },
    {
      headers: getRequiredAuthHeaders(),
      withCredentials: true,
    }
  );

  return data;
}
